import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../../context/AuthContext';

/**
 * Per-user chat preferences (WhatsApp-style):
 * starred messages, favourite chats, pinned chats, custom display names
 * (aliases), muted chats, blocked chats and custom lists.
 *
 * Persisted server-side (per infinityCode) via /api/chat-preferences so they
 * survive logout and follow the user across devices. localStorage is kept as
 * a fast local cache for instant rendering before the server round-trip.
 * These are personal view-layer settings — they intentionally do NOT affect
 * the other party.
 */

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://nexploy-eg.com';

const emptyPrefs = {
  // Array of message snapshots: { id, chatId, chatName, text, type,
  // mediaName, senderName, sent, time, timestamp }
  starredMessages: [],
  favorites: [],   // chat ids
  pinned: [],      // chat ids
  aliases: {},     // chatId -> custom display name
  muted: [],       // chat ids
  blocked: [],     // chat ids
  lists: {},       // listName -> [chatIds]
};

const storageKey = (userCode) => `messengerPrefs_${userCode || 'anon'}`;

const normalize = (parsed) =>
  ({ ...emptyPrefs, ...(parsed && typeof parsed === 'object' ? parsed : {}) });

const readLocalPrefs = (userCode) => {
  try {
    const raw = localStorage.getItem(storageKey(userCode));
    if (!raw) return { ...emptyPrefs };
    return normalize(JSON.parse(raw));
  } catch {
    return { ...emptyPrefs };
  }
};

const writeLocalPrefs = (userCode, prefs) => {
  try {
    localStorage.setItem(storageKey(userCode), JSON.stringify(prefs));
  } catch {
    // Quota/private-mode failures degrade to server-only persistence.
  }
};

const hasAnyPrefs = (prefs) =>
  Object.values(prefs).some((v) =>
    Array.isArray(v) ? v.length > 0 : v && typeof v === 'object' && Object.keys(v).length > 0
  );

const SAVE_DEBOUNCE_MS = 800;

const useChatPrefs = (userCode) => {
  const [prefs, setPrefs] = useState(() => readLocalPrefs(userCode));
  // Server writes are blocked until the initial fetch finishes so a slow GET
  // can never be overwritten by an empty/stale local state.
  const loadedRef = useRef(false);
  const saveTimerRef = useRef(null);
  const latestPrefsRef = useRef(prefs);

  const pushToServer = useCallback(async (next) => {
    try {
      await apiFetch(`${API_BASE_URL}/api/chat-preferences`, {
        method: 'PUT',
        body: JSON.stringify({ prefs: next }),
      });
    } catch (err) {
      console.warn('⚠️ Failed to save chat preferences to server:', err);
    }
  }, []);

  const scheduleSave = useCallback((next) => {
    latestPrefsRef.current = next;
    if (!loadedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      pushToServer(latestPrefsRef.current);
    }, SAVE_DEBOUNCE_MS);
  }, [pushToServer]);

  // Load from the server when the logged-in user changes.
  useEffect(() => {
    loadedRef.current = false;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    const local = readLocalPrefs(userCode);
    setPrefs(local);
    latestPrefsRef.current = local;

    if (!userCode) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/chat-preferences`, { method: 'GET' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (cancelled) return;

        const server = normalize(body?.data);
        loadedRef.current = true;

        if (!hasAnyPrefs(server) && hasAnyPrefs(local)) {
          // First sync for this account: migrate device-local prefs up.
          pushToServer(local);
          return;
        }

        setPrefs(server);
        latestPrefsRef.current = server;
        writeLocalPrefs(userCode, server);
      } catch (err) {
        console.warn('⚠️ Failed to load chat preferences from server:', err);
        // Offline/error: allow saves anyway so the user's changes eventually
        // reach the server once connectivity returns on a later change.
        loadedRef.current = true;
      }
    })();

    return () => { cancelled = true; };
  }, [userCode, pushToServer]);

  // Flush a pending debounced save on unmount.
  useEffect(() => () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      pushToServer(latestPrefsRef.current);
    }
  }, [pushToServer]);

  const update = useCallback((updater) => {
    setPrefs((prev) => {
      const next = updater(prev);
      writeLocalPrefs(userCode, next);
      scheduleSave(next);
      return next;
    });
  }, [userCode, scheduleSave]);

  const toggleInList = useCallback((listName, chatId) => {
    update((prev) => {
      const list = prev[listName] || [];
      const id = String(chatId);
      const next = list.some((x) => String(x) === id)
        ? list.filter((x) => String(x) !== id)
        : [...list, id];
      return { ...prev, [listName]: next };
    });
  }, [update]);

  const has = useCallback(
    (listName, chatId) => (prefs[listName] || []).some((x) => String(x) === String(chatId)),
    [prefs]
  );

  const toggleStarMessage = useCallback((snapshot) => {
    update((prev) => {
      const exists = prev.starredMessages.some((m) => String(m.id) === String(snapshot.id));
      const starredMessages = exists
        ? prev.starredMessages.filter((m) => String(m.id) !== String(snapshot.id))
        : [...prev.starredMessages, { ...snapshot, starredAt: Date.now() }];
      return { ...prev, starredMessages };
    });
  }, [update]);

  const isStarred = useCallback(
    (messageId) => prefs.starredMessages.some((m) => String(m.id) === String(messageId)),
    [prefs.starredMessages]
  );

  const setAlias = useCallback((chatId, alias) => {
    update((prev) => {
      const aliases = { ...prev.aliases };
      const trimmed = (alias || '').trim();
      if (trimmed) aliases[String(chatId)] = trimmed;
      else delete aliases[String(chatId)];
      return { ...prev, aliases };
    });
  }, [update]);

  const getAlias = useCallback(
    (chatId) => prefs.aliases[String(chatId)] || null,
    [prefs.aliases]
  );

  const addToCustomList = useCallback((listName, chatId) => {
    update((prev) => {
      const lists = { ...prev.lists };
      const list = lists[listName] || [];
      const id = String(chatId);
      if (!list.includes(id)) lists[listName] = [...list, id];
      return { ...prev, lists };
    });
  }, [update]);

  return {
    prefs,
    toggleStarMessage,
    isStarred,
    toggleFavorite: (chatId) => toggleInList('favorites', chatId),
    isFavorite: (chatId) => has('favorites', chatId),
    togglePinned: (chatId) => toggleInList('pinned', chatId),
    isPinned: (chatId) => has('pinned', chatId),
    toggleMuted: (chatId) => toggleInList('muted', chatId),
    isMuted: (chatId) => has('muted', chatId),
    toggleBlocked: (chatId) => toggleInList('blocked', chatId),
    isBlocked: (chatId) => has('blocked', chatId),
    setAlias,
    getAlias,
    addToCustomList,
  };
};

export default useChatPrefs;
